/**
 * Content Moderation Utility (Frontend)
 * Checks user input for inappropriate content that is not suitable for minors
 */

// List of inappropriate keywords/phrases that should trigger content moderation
// Content not suitable for users under 18
const INAPPROPRIATE_KEYWORDS = [
  // Sexual content
  'sexual', 'sex', 'nude', 'nudity', 'naked', 'nakedness',
  'bikini', 'underwear', 'lingerie', 'bra', 'panties',
  'porn', 'pornography', 'explicit', 'erotic', 'sexy',
  'orgasm', 'masturbat', 'intercourse', 'penetration',
  'genital', 'genitals', 'breast', 'breasts', 'nipple',
  'vagina', 'penis', 'dick', 'cock', 'pussy', 'ass',
  'butt', 'buttock', 'anus', 'oral sex', 'anal',
  
  // Racist content
  'nigger', 'nigga', 'nazi', 'kkk', 'klan',
  'racial slur', 'racist', 'racism', 'hate speech',
  'white supremacy', 'supremacist', 'holocaust denial',
  
  // Violence (inappropriate for minors)
  'kill', 'murder', 'violence', 'blood', 'gore',
  'torture', 'suicide', 'self-harm', 'weapon',
  'gun', 'knife', 'bomb', 'terrorist', 'terrorism',
  
  // Drugs (inappropriate for minors)
  'drug', 'cocaine', 'heroin', 'marijuana', 'weed',
  'cannabis', 'meth', 'methamphetamine', 'ecstasy',
  'lsd', 'hallucinogen', 'opioid', 'addiction',
];

/**
 * Check if user input contains inappropriate content
 * @param {string} userInput - The user's input text to check
 * @returns {Object} - { isInappropriate: boolean, matchedKeywords: string[] }
 */
export function checkContentModeration(userInput) {
  if (!userInput || typeof userInput !== 'string') {
    return { isInappropriate: false, matchedKeywords: [] };
  }

  const lowerInput = userInput.toLowerCase();
  const matchedKeywords = [];

  // Check for inappropriate keywords
  for (const keyword of INAPPROPRIATE_KEYWORDS) {
    // Use word boundary matching to avoid false positives
    const regex = new RegExp(`\\b${keyword}\\w*\\b`, 'gi');
    if (regex.test(lowerInput)) {
      matchedKeywords.push(keyword);
    }
  }

  const isInappropriate = matchedKeywords.length > 0;

  if (isInappropriate) {
    console.warn('Content moderation triggered', {
      matchedKeywords,
      inputLength: userInput.length,
      inputPreview: userInput.substring(0, 100)
    });
  }

  return {
    isInappropriate,
    matchedKeywords
  };
}

/**
 * Get user-friendly error message for inappropriate content
 * @param {string} context - Optional context (e.g., 'character', 'scene', 'story')
 * @param {string[]} matchedKeywords - Array of keywords that triggered moderation
 * @returns {string} - Error message to display to user
 */
export function getContentModerationMessage(context = '', matchedKeywords = []) {
  const contextText = context ? ` for ${context} generation` : '';
  let message = `The content of the story${contextText} is not as per the Artmosphere norms.`;
  
  if (matchedKeywords && matchedKeywords.length > 0) {
    // Remove duplicates and format keywords
    const uniqueKeywords = [...new Set(matchedKeywords)];
    const highlightedKeywords = uniqueKeywords.map(kw => `"${kw}"`).join(', ');
    message += ` Please remove or modify the following words: ${highlightedKeywords}.`;
  }
  
  message += ' Please ensure your content is appropriate for all audiences.';
  return message;
}
